import { EnhancedStore, MiddlewareArray } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { AnyAction } from "redux";
import { ThunkMiddleware } from "redux-thunk";
import { AuthType, getAdminApiTokenAsync, getAuthTokenAsync } from "./store/auth-slice";
import { BookWorkspaceType } from "./store/book-workspace-slice";
import { GraphType } from "./store/graph-slice";
import { RootState } from "./store/store";
import { TabViewType } from "./store/tab-view-slice";
import axiosRetry from 'axios-retry';
import { Constant } from "./utils/constant";
import { StateIndicator } from "./models/shared/Enums/StateIndicator";


let store: EnhancedStore<{ tabView: TabViewType; auth: AuthType; graph: GraphType, bookWorkspace: BookWorkspaceType }, AnyAction, MiddlewareArray<[ThunkMiddleware<{ tabView: TabViewType; auth: AuthType; graph: GraphType, bookWorkspace: BookWorkspaceType }, AnyAction, undefined>]>>;

export const injectStore = (_store: EnhancedStore<{ tabView: TabViewType; auth: AuthType; graph: GraphType, bookWorkspace: BookWorkspaceType }, AnyAction, MiddlewareArray<[ThunkMiddleware<{ tabView: TabViewType; auth: AuthType; graph: GraphType, bookWorkspace: BookWorkspaceType }, AnyAction, undefined>]>>) => {
  store = _store;
}


const httpClient = axios.create({
  baseURL: "https://condeco-apis-dev.azure-api.net/miniSyncDev/api/V1/",
  headers: {
    "Content-type": "application/json",
    "Ocp-Apim-Subscription-Key": "",
    Authorization: "",
  },
});

axiosRetry(httpClient, {
  retries: Constant.ApiRetryCount,
  retryDelay: (retryCount) => {
    return retryCount * Constant.ApiRetryBackOffInterval;
  },
  retryCondition: (error : AxiosError) => { 
    return error.response !== undefined && error.response.status !== 0
    ? error.response.status === 401 || error.response.status === 502
    : (error.code === AxiosError.ERR_NETWORK ? true: false)},
  shouldResetTimeout: true,
  onRetry: async (retryCount, error, requestConfig) => {
    let state: RootState = store.getState();
    if (state.auth.adminApiTokenStatus !== StateIndicator.Loading) {
      // A network error means the cached admin API URL likely moved (e.g. migration),
      // so force a fresh resolve; 401/502 stay on the normal token refresh.
      const isNetworkError = error.code === AxiosError.ERR_NETWORK;
      store.dispatch(getAuthTokenAsync())
      state = store.getState();
      const email = state.auth.email;
      const ssoToken = state.auth.ssoToken;
      store.dispatch(getAdminApiTokenAsync({ ssoToken, email, forceRefresh: isNetworkError }));
    }
  },
});

httpClient.interceptors.request.use(
  async (config) => {
    const state: RootState = store.getState();
    const ocpKey = state.auth.adminApi?.ocpKey;
    const token = state.auth.adminApi?.access_Token;
    config.baseURL = state.auth.adminApi?.adminApiUrl;
    config.headers = {
      "Ocp-Apim-Subscription-Key": ocpKey!,
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Content-Security-Policy": "default-src 'self'",
      "X-Frame-Options": "DENY",
      "Strict-Transport-Security": "max-age=31536000; includeSubDomains; preload",
      "X-Content-Type-Options": "nosniff"
    };
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

const processorClient = axios.create({
  baseURL: process.env.REACT_APP_FUNCTION_APP_URL,
});



processorClient.interceptors.request.use(
  async (config) => {
    const state: RootState = store.getState();
    config.headers = {
      Authorization: `Bearer ${state.auth.ssoToken}`,
      "x-functions-key": String(process.env.REACT_APP_ENVIRONMENT_KEY),
      "Content-Type": "application/json",
      "Content-Security-Policy": "default-src 'self'",
      "X-Frame-Options": "DENY",
      "Strict-Transport-Security": "max-age=31536000; includeSubDomains; preload",
      "X-Content-Type-Options": "nosniff",
      "region": String(process.env.REACT_APP_TAB_REG_PRIMARY_LOC)
    };
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

processorClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    const state = store.getState();
    const originalReq = error?.response;
    if (originalReq != null && originalReq !== undefined && originalReq.status === 401) {
      await store.dispatch(getAuthTokenAsync()).unwrap();
      processorClient.defaults.headers.common["Authorization"] = `Bearer ${state.auth.ssoToken}`
      return processorClient;
    }
    return Promise.reject(error);
  }
);

const authClient = axios.create({
  baseURL: process.env.REACT_APP_BOT_APP_URL,
});

authClient.interceptors.request.use(
  async (config) => {
    const state: RootState = store.getState();
    config.headers = {
      Authorization: `Bearer ${state.auth.ssoToken}`,
      "Content-Type": "application/json",
      "Content-Security-Policy": "default-src 'self'",
      "X-Frame-Options": "DENY",
      "Strict-Transport-Security": "max-age=31536000; includeSubDomains; preload",
      "X-Content-Type-Options": "nosniff",
      "region": String(process.env.REACT_APP_TAB_REG_PRIMARY_LOC)
    };
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

const enterpriseClient = axios.create({
  baseURL: "",
  headers: {
    "Content-type": "application/json",    
    Authorization: "",
  },
});

enterpriseClient.interceptors.request.use(
  async (config) => {
    const state: RootState = store.getState();
    const token = state.auth.adminApi?.access_Token;
    config.baseURL = state.auth.adminApi?.enterpriseBaseUrl;
    config.headers = {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
      "Content-Security-Policy": "default-src 'self'",
      "X-Frame-Options": "DENY",
      "Strict-Transport-Security": "max-age=31536000; includeSubDomains; preload",
      "X-Content-Type-Options": "nosniff"
    };
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

// enterpriseClient has no retry of its own; mirror httpClient so a moved
// enterpriseBaseUrl (migration) is force-resolved and the call retried against the new URL.
axiosRetry(enterpriseClient, {
  retries: Constant.ApiRetryCount,
  retryDelay: (retryCount) => {
    return retryCount * Constant.ApiRetryBackOffInterval;
  },
  retryCondition: (error: AxiosError) => {
    return error.response !== undefined && error.response.status !== 0
      ? error.response.status === 401 || error.response.status === 502
      : (error.code === AxiosError.ERR_NETWORK ? true : false);
  },
  shouldResetTimeout: true,
  onRetry: async (retryCount, error, requestConfig) => {
    let state: RootState = store.getState();
    if (state.auth.adminApiTokenStatus !== StateIndicator.Loading) {
      const isNetworkError = error.code === AxiosError.ERR_NETWORK;
      store.dispatch(getAuthTokenAsync());
      state = store.getState();
      const email = state.auth.email;
      const ssoToken = state.auth.ssoToken;
      store.dispatch(getAdminApiTokenAsync({ ssoToken, email, forceRefresh: isNetworkError }));
    }
  },
});

const httpCommonClients = {
  httpClient,
  processorClient,
  authClient,
  enterpriseClient,
};
export default httpCommonClients;
